import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import logo from './images/logo.png';
import { company, services } from './data';
import { Container } from './components/ui';
import Contact from './components/Contact';
import Home from './pages/home';
import AboutUs from './pages/aboutus';
import CareerPage from './pages/career';
import Gallery from './pages/gallery';
import InsightsPage from './blog/pages/InsightsPage';
import ContentPage from './blog/pages/ContentPage';
import PreviewList from './blog/pages/PreviewList';
import PreviewPage from './blog/pages/PreviewPage';
import PreviewGuard from './blog/preview/PreviewGuard';
import StatusMessage from './blog/components/StatusMessage';
import { resetSeo } from './blog/content/seo';
import './blog/blog.css';

const DEFAULT_TITLE = 'Anuryan E&A Pvt. Ltd.';
const DEFAULT_DESCRIPTION = 'Anuryan E & A Pvt. Ltd. - ACB servicing, VFD installation and commissioning, PLC & HMI programming, digitization and industrial IoT. Kolkata, since 2000.';
const NAV = [
  { label: 'Home', to: '/' },
  { label: 'Services', to: '/', anchor: 'services' },
  { label: 'Industries', to: '/', anchor: 'industries' },
  { label: 'Insights', to: '/insights' },
  { label: 'Gallery', to: '/gallery' },
  { label: 'About', to: '/about' },
  { label: 'Careers', to: '/careers' },
];
// Pages that open with a dark full-width hero, so the header can stay transparent at the top.
const HERO_PATHS = ['/', '/about', '/careers', '/insights', '/gallery'];

function App() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const isPreview = pathname.startsWith('/preview');
  const hasHero = HERO_PATHS.includes(pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    if (!window.location.hash) window.scrollTo({ top: 0 });
    // Article pages set their own <head>; on any other page restore the site defaults.
    if (HERO_PATHS.includes(pathname) && pathname !== '/insights' && pathname !== '/gallery') resetSeo(DEFAULT_TITLE, DEFAULT_DESCRIPTION);
  }, [pathname]);

  // go('/about') changes page; go('contact' | 'services' | 'industries') scrolls to a section, switching to home first if needed.
  const go = useCallback((target) => {
    setOpen(false);
    const scrollTo = (id) => setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 80);
    if (target.startsWith('/')) {
      navigate(target);
    } else if (target === 'contact') {
      scrollTo('contact');
    } else {
      if (pathname !== '/') navigate('/');
      scrollTo(target);
    }
  }, [navigate, pathname]);

  const solidHeader = scrolled || open || !hasHero;

  return (
    <div className="font-sans text-ink">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[60] bg-white text-secondary px-4 py-2 rounded">Skip to content</a>
      <header className={`fixed inset-x-0 top-0 z-50 transition ${solidHeader ? 'bg-ink/95 backdrop-blur border-b border-white/10' : 'bg-transparent'}`}>
        <Container className="flex items-center justify-between h-20 md:h-24">
          <Link to="/" className="flex items-center gap-3 text-white" aria-label={`${company.name} home`}>
            <span className="bg-white rounded-xl p-1 w-14 h-14 md:w-16 md:h-16 grid place-items-center">
              <img src={logo} alt="" className="w-full h-full object-contain" />
            </span>
            <span className="font-display font-semibold text-lg md:text-xl leading-tight text-left whitespace-nowrap">
              Anuryan <span className="text-[#4cc3ff]">E&amp;A</span> Pvt. Ltd.
              <span className="hidden xl:block font-sans font-normal text-xs text-white/55 tracking-wide mt-0.5">Automation · Switchgear · Drives · Industrial IoT</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Main">
            {NAV.map((n) => (
              <button key={n.label} onClick={() => go(n.anchor || n.to)}
                className={`px-2.5 xl:px-3.5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${!n.anchor && (n.to === '/' ? pathname === '/' : pathname.startsWith(n.to)) ? 'text-white bg-white/10' : 'text-white/75 hover:text-white hover:bg-white/10'}`}>
                {n.label}
              </button>
            ))}
            <button onClick={() => go('contact')} className="ml-3 whitespace-nowrap rounded-full bg-primary hover:bg-[#0aa0ee] text-white text-sm font-semibold px-5 py-2.5 transition">Contact us</button>
          </nav>

          <button className="lg:hidden text-white p-2" onClick={() => setOpen(!open)} aria-label="Toggle menu" aria-expanded={open}>
            {open ? <FaTimes size={22} /> : <FaBars size={22} />}
          </button>
        </Container>
        {open && (
          <nav className="lg:hidden border-t border-white/10 px-5 py-4 flex flex-col" aria-label="Mobile">
            {[...NAV, { label: 'Contact', to: 'contact' }].map((n) => (
              <button key={n.label} onClick={() => go(n.anchor || n.to)} className="text-left text-white/90 py-3 border-b border-white/10 last:border-0">{n.label}</button>
            ))}
          </nav>
        )}
      </header>

      <main id="main">
        <Routes>
          <Route path="/" element={<Home go={go} />} />
          <Route path="/about" element={<AboutUs go={go} />} />
          <Route path="/careers" element={<CareerPage />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/insights" element={<div className="blog-scope"><InsightsPage /></div>} />
          <Route path="/preview" element={<div className="blog-scope blog-top"><PreviewGuard><PreviewList /></PreviewGuard></div>} />
          <Route path="/preview/:slug" element={<div className="blog-scope blog-top"><PreviewGuard><PreviewPage /></PreviewGuard></div>} />
          <Route path="/:typeRoute/:slug" element={<div className="blog-scope blog-top"><ContentPage /></div>} />
          <Route path="*" element={<div className="blog-scope blog-top"><div className="blog-container"><StatusMessage title="Page not found">The page you are looking for does not exist.</StatusMessage></div></div>} />
        </Routes>
      </main>
      {!isPreview && <Contact />}

      <footer className="bg-ink text-white/70 border-t border-white/10">
        <Container className="py-14 grid md:grid-cols-3 gap-10">
          <div>
            <p className="font-display font-semibold text-xl text-white">{company.name}</p>
            <p className="mt-3 text-sm max-w-xs">Industrial automation and switchgear solutions from Kolkata, since {company.founded}.</p>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-[#4cc3ff] mb-4">Services</p>
            <ul className="space-y-2 text-sm">
              {services.map((s) => (
                <li key={s.id}><button onClick={() => go('services')} className="hover:text-white text-left">{s.title}</button></li>
              ))}
              <li><Link to="/insights" className="hover:text-white">Insights &amp; articles</Link></li>
              <li><Link to="/gallery" className="hover:text-white">Gallery</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-[#4cc3ff] mb-4">Reach us</p>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-3"><FaPhoneAlt className="mt-1 shrink-0" /><a href={company.phoneHref} className="hover:text-white">{company.phone}</a></li>
              <li className="flex gap-3"><FaEnvelope className="mt-1 shrink-0" /><a href={`mailto:${company.email}`} className="hover:text-white">{company.email}</a></li>
              <li className="flex gap-3"><FaMapMarkerAlt className="mt-1 shrink-0" /><span>{company.address}</span></li>
            </ul>
          </div>
        </Container>
        <div className="border-t border-white/10 py-5 text-center text-xs text-white/40">
          © {new Date().getFullYear()} {company.name} All rights reserved.
        </div>
      </footer>
    </div>
  );
}

export default App;
